import {
  type FC,
  type ReactNode,
  useCallback,
  type RefObject,
  useMemo,
  useState,
} from 'react';
import { ActionButton } from '../../../../common/components';
import {
  confirmPanelFormClose,
  Form,
  type FormRef,
  useAlertContext,
} from '../../../../common/form';
import { useMutationErrors } from '../../../../common/queryClient';
import { confirmationHeaders } from '../../../../common/utils';
import { GetApiErrorMessage } from '../../../../common/utils/ValidationMessagesMap';
import { useSystemClassifierItemsBy } from '../../../../configuration';
import { type AlgorithmJoinFormDataType } from '../../joins/schema/types';
import type { SourceDimensionsMap } from '../../joins/useAlgorithmJoinsDataQuery';
import type { AlgorithmMappingQueryDtoType } from '../../mappings/schema/types';
import type { AlgorithmSourceFormDataType } from '../../sources/schema/types';
import { useAlgorithmJoinsAndMappingsMutation } from '../query/useAlgorithmJoinsAndMappingsMutation';
import { useAlgorithmJoinsAndMappingsGraphFormDataSchema } from '../schema/AlgorithmJoinsAndMappingsGraphFormDataSchema';
import { AlgorithmJoinsAndMappingsGraphValidFormDataSchema } from '../schema/AlgorithmJoinsAndMappingsGraphValidFormDataSchema';
import type { AlgorithmJoinsAndMappingsGraphValidFormDataType } from '../schema/types';
import { AlgorithmJoinsWithMappingsGraphModal } from './AlgorithmJoinsWithMappingsGraphModal';
import { AlgorithmJoinsWithMappingsGraph } from './AlgorithmJoinsWithMappingsGraph';
import { i18nNsFactory } from '@atlas-frontend/i18n';
import validationTranslations from '../../../translations/validation.translations.json';
import { useWatch } from 'react-hook-form';

const { addTranslations, t } = i18nNsFactory(
  'algorithm-joins-with-mappings-graph-modal',
);
addTranslations(validationTranslations);

const translateMutationError: GetApiErrorMessage = (code, meta) =>
  t('alert.error.code.' + code, meta?.args && { ...meta.args });

interface AlgorithmJoinsWithMappingsGraphFormModalBaseProps {
  joins: AlgorithmJoinFormDataType[];
  sources: AlgorithmSourceFormDataType[];
  sourceDimensionsMap: SourceDimensionsMap;
  mappings: AlgorithmMappingQueryDtoType[];
  onClose?: () => void;
  onChange?: () => void;
  actions?: ReactNode;
  transition?: boolean;
  readOnly?: boolean;
}

export type AlgorithmJoinsWithMappingsGraphFormModalProps =
  AlgorithmJoinsWithMappingsGraphFormModalBaseProps &
    (
      | {
          readOnly?: false;
          algorithmId: number;
          formRef: RefObject<
            FormRef<AlgorithmJoinsAndMappingsGraphValidFormDataType>
          >;
        }
      | {
          readOnly?: true;
          algorithmId?: never;
          formRef?: never;
        }
    );

export const AlgorithmJoinsWithMappingsGraphFormModal: FC<
  AlgorithmJoinsWithMappingsGraphFormModalProps
> = (props) => {
  const {
    algorithmId,
    sources,
    sourceDimensionsMap,
    joins,
    mappings,
    onClose,
    onChange,
    actions,
    transition = true,
    readOnly = false,
    formRef,
  } = props;

  // render graph only when modal is fully opened (transition has ended) to avoid react-flow rendering issues
  const [isModalReady, setIsModalReady] = useState(false);
  const typeCode = useWatch({ name: 'typeCode' });

  const handleCancel = useCallback(() => {
    if (formRef?.current.isBlocked) {
      confirmPanelFormClose(onClose);
    } else {
      onClose?.();
    }
  }, [formRef, onClose]);

  const { mutateAsync } = useAlgorithmJoinsAndMappingsMutation();
  const { mutationErrors, setMutationErrors, clearMutationErrors } =
    useMutationErrors(translateMutationError);
  const { resetAlert, setSuccess } = useAlertContext();

  const handleSubmit = useCallback(
    async (
      values: AlgorithmJoinsAndMappingsGraphValidFormDataType,
      confirm: boolean,
    ) => {
      resetAlert();
      clearMutationErrors();
      return mutateAsync({
        id: algorithmId,
        data: values,
        headers: confirmationHeaders(confirm),
      })
        .then(() => {
          onChange?.();
          onClose?.();
          setSuccess();
          formRef?.current.supressBlock();
        })
        .catch((error) => {
          setMutationErrors(error?.response?.data?.errors ?? []);
        });
    },
    [
      algorithmId,
      clearMutationErrors,
      formRef,
      mutateAsync,
      onChange,
      onClose,
      resetAlert,
      setMutationErrors,
      setSuccess,
    ],
  );

  const classifierDataTypesById = useSystemClassifierItemsBy(
    ['CLASSIFIER_DATA_TYPE'],
    'id',
  ).CLASSIFIER_DATA_TYPE;

  const AlgorithmJoinsAndMappingsGraphFormDataSchema =
    useAlgorithmJoinsAndMappingsGraphFormDataSchema();

  const initialValues = useMemo(() => {
    const { nodes, edges } = AlgorithmJoinsAndMappingsGraphFormDataSchema.parse(
      { joins, mappings, sourceDimensionsMap },
    );
    return {
      nodes,
      edges,
      sources,
      classifierDataTypesById,
    };
  }, [
    AlgorithmJoinsAndMappingsGraphFormDataSchema,
    joins,
    mappings,
    classifierDataTypesById,
    sources,
    sourceDimensionsMap,
  ]);

  return (
    <Form
      schema={AlgorithmJoinsAndMappingsGraphValidFormDataSchema}
      defaultValues={initialValues}
      formRef={formRef}
      onSubmit={handleSubmit}
      serverErrors={mutationErrors}
    >
      <AlgorithmJoinsWithMappingsGraphModal
        transition={transition}
        actions={actions}
        onClose={handleCancel}
        footer={
          <>
            <ActionButton
              type="abort"
              layoutType={readOnly ? 'detail' : 'mutate'}
              onClick={handleCancel}
            />
            {!readOnly && (
              <ActionButton type="formSubmit" layoutType="mutate" />
            )}
          </>
        }
        readOnly={readOnly}
        afterOpenChange={setIsModalReady}
      >
        {isModalReady && (
          <AlgorithmJoinsWithMappingsGraph
            algorithmType={typeCode}
            sources={sources}
            readOnly={readOnly}
          />
        )}
      </AlgorithmJoinsWithMappingsGraphModal>
    </Form>
  );
};
